import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

import { IngredientReferenceUnifiedScoreSchema } from "src/api/client-api/ingredientReferenceUnified/types";
import { hydraSearchToAppSearch } from "src/api/_common/hydra/search";

// Zod schema for unified entity (ingredient or intermediate product)
const UnifiedIngredientSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    clientId: z.string().nullable().optional(),
    origin: z.string().nullable().optional(),
    type: z.enum(["ingredient", "intermediate-product"]),
    ingredientLine: z
        .object({
            name: z.string(),
        })
        .nullable()
        .optional(),
    scores: IngredientReferenceUnifiedScoreSchema.array().optional().default([]),
});

export type UnifiedIngredient = z.infer<typeof UnifiedIngredientSchema>;

type AxiosParams = Record<string, string | number | boolean | undefined>;

type Options = {
    params?: AxiosParams;
    search?: string;
    cacheTime?: number;
    enabled?: boolean;
};

export const unifiedIngredientListKey = "unifiedIngredientList" as const;

export const useGetUnifiedIngredientList = (options?: Options) => {
    const { params = {}, search = "", cacheTime = 0, enabled = true } = options || {};

    const query = useQuery({
        queryKey: [unifiedIngredientListKey, params, search],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled,
        queryFn: async () => {
            const url = `/api/unified-ingredient-list`;
            const response = await loggedInApiCall.get(url, {
                params: {
                    ...params,
                    q: search !== "" ? search : undefined,
                },
            });

            return response;
        },
        select: (request) => {
            try {
                const totalItems = request.data["hydra:totalItems"];
                const rawData = request.data["hydra:member"];
                const appSearch = hydraSearchToAppSearch(request.data["hydra:search"]);

                const data = zodParseWithLog(rawData, UnifiedIngredientSchema.array(), unifiedIngredientListKey);

                return {
                    items: data,
                    totalItems: totalItems,
                    appSearch,
                };
            } catch (error) {
                return {
                    items: [],
                    totalItems: 0,
                    appSearch: {},
                };
            }
        },
    });

    const isReady = isQueryReady(query, enabled);

    return {
        unifiedIngredients: query.data?.items,
        totalItems: query.data?.totalItems,
        isReady,
        appSearch: query.data?.appSearch ?? {},
        ...query,
    };
};
